"use client";

import { useCallback, useRef, useState } from "react";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { useResponsiveChatOpen } from "@hooks/useResponsiveChatOpen";
import { fetchHasHoldingsWithRecentRetry } from "../api/probeHasHoldings";
import type { OverlapEntryContext } from "../types";
import { buildEtfOverlapChatMessage } from "../utils/buildEtfOverlapChatMessage";
import { setPendingOverlapContext } from "../utils/overlapChatContext";

export interface UseOverlapEntryRouterOptions {
  /**
   * 无持仓（含探测失败 / 超时）时的回调。由调用方注入（Slice 5 提供 `openEtfOverlapGuideSheet`），
   * 本 hook 因此不依赖引导弹窗，Slice 1 可独立编译。
   */
  onNoHoldings: (ctx: OverlapEntryContext) => void;
  returnToBrief?: boolean;
}

export type OverlapRouteResult = "chat" | "guide" | "ignored";

export interface OverlapRouteOptions {
  /**
   * 回显气泡（协议行 label）文案。后端配置驱动的推荐问题传该项返回的标题；
   * 缺省 / 空白回退共享常量，Brief 卡、Invest 入口、广告登录承接等其他入口行为不变。
   */
  label?: string;
  /**
   * 调用方已确知用户有持仓（如 Portfolio 页入口卡只在总览有数据时才渲染）：跳过探测直接进 Chat，
   * `overlap_entry_tap.has_holdings` 仍按 true 上报（review #122）。
   */
  knownHasHoldings?: boolean;
}

/**
 * 入口分流（FD-1）：查持仓 → 有持仓直接进 Chat 发协议行，无持仓交给 `onNoHoldings`。
 * 探测失败按无持仓（`fetchHasHoldings` 内部兜底，不抛错）；刚上传 / 连接后读空会先短重试一次（索引延迟）。
 * 埋点 `overlap_entry_tap` 由**调用方元素**声明式发，本 hook 不发（规范：不要在 onClick 里手写第二次上报）。
 */
export const useOverlapEntryRouter = ({
  onNoHoldings,
  returnToBrief,
}: UseOverlapEntryRouterOptions) => {
  const { memberApi } = useOspApiClientProvider();
  const { openResponsiveChat } = useResponsiveChatOpen();
  const routingRef = useRef(false);
  const [isRouting, setIsRouting] = useState(false);

  const routeToOverlap = useCallback(
    async (
      ctx: OverlapEntryContext,
      options?: OverlapRouteOptions,
    ): Promise<OverlapRouteResult> => {
      // pending 守卫：探测进行中重复点击直接忽略，避免连发两条协议行
      if (routingRef.current) {
        return "ignored";
      }
      routingRef.current = true;
      setIsRouting(true);
      try {
        const hasHoldings =
          options?.knownHasHoldings === true ||
          (await fetchHasHoldingsWithRecentRetry(memberApi));
        if (hasHoldings) {
          // 结果曝光时消费：这次结果来自哪个入口、输入是已有持仓（EDG-561 `overlap_result_view`）
          setPendingOverlapContext({
            entry_source: ctx.entry_source,
            input_source: "existing",
          });
          await openResponsiveChat({
            question: buildEtfOverlapChatMessage(
              options?.label?.trim() || undefined,
            ),
            autoSubmit: true,
            ...(returnToBrief ? { returnToBrief: true } : {}),
          });
          return "chat";
        }
        onNoHoldings(ctx);
        return "guide";
      } finally {
        routingRef.current = false;
        setIsRouting(false);
      }
    },
    [memberApi, onNoHoldings, openResponsiveChat, returnToBrief],
  );

  return { routeToOverlap, isRouting };
};
